<template>
  <div
    v-if="autoPayPauseModal.visible"
    class="auto-pay-pause-mask"
    @click.self="handleOk"
  >
    <div class="auto-pay-pause-modal" role="dialog" aria-modal="true" aria-labelledby="auto-pay-pause-title">
      <h2 id="auto-pay-pause-title" class="auto-pay-pause-title">
        {{ autoPayPauseModal.title }}
      </h2>

      <p
        v-for="(paragraph, index) in messageParagraphs"
        :key="index"
        class="auto-pay-pause-paragraph"
      >
        <template v-for="(segment, segIndex) in parseBoldSegments(paragraph)" :key="segIndex">
          <strong v-if="segment.type === 'bold'">{{ segment.content }}</strong>
          <template v-else>{{ segment.content }}</template>
        </template>
      </p>

      <label class="auto-pay-pause-checkbox">
        <input v-model="dontShowAgain" type="checkbox" />
        <span>Don't show again</span>
      </label>

      <button type="button" class="auto-pay-pause-ok" @click="handleOk">
        OK
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useDriverStore } from '@/store/driver/driver';

const driverStore = useDriverStore();
const { autoPayPauseModal } = storeToRefs(driverStore);
const { closeAutoPayPauseModal } = driverStore;

const dontShowAgain = ref(false);

watch(
  () => autoPayPauseModal.value.visible,
  (visible) => {
    if (visible) dontShowAgain.value = false;
  }
);

const messageParagraphs = computed(() => {
  const message = autoPayPauseModal.value.message;
  if (!message) return [];
  return message.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
});

function parseBoldSegments(text) {
  const segments = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }
    segments.push({ type: 'bold', content: match[1] });
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', content: text.slice(lastIndex) });
  }

  return segments.length ? segments : [{ type: 'text', content: text }];
}

const handleOk = async () => {
  await closeAutoPayPauseModal(dontShowAgain.value);
  dontShowAgain.value = false;
};
</script>

<style scoped>
.auto-pay-pause-mask {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(5, 28, 44, 0.45);
}

.auto-pay-pause-modal {
  width: 100%;
  max-width: 720px;
  padding: 32px 36px 28px;
  background: #fff;
  border-radius: 12px;
  text-align: left;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.auto-pay-pause-title {
  margin: 0 0 24px;
  font-family: 'Euclid Square', sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  color: #051c2c;
}

.auto-pay-pause-paragraph {
  margin: 0 0 20px;
  font-family: 'Mulish', sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.45;
  color: #051c2c;
}

.auto-pay-pause-paragraph:last-of-type {
  margin-bottom: 28px;
}

.auto-pay-pause-paragraph strong {
  font-weight: 700;
}

.auto-pay-pause-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 28px;
  cursor: pointer;
  font-family: 'Mulish', sans-serif;
  font-size: 18px;
  color: #051c2c;
  user-select: none;
}

.auto-pay-pause-checkbox input {
  width: 18px;
  height: 18px;
  margin: 0;
  accent-color: #051c2c;
  cursor: pointer;
}

.auto-pay-pause-ok {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 88px;
  padding: 10px 28px;
  border: 1px solid #c5cdd6;
  border-radius: 8px;
  background: #e8eef4;
  font-family: 'Euclid Square', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #051c2c;
  cursor: pointer;
  transition: background-color 0.2s;
}

.auto-pay-pause-ok:hover {
  background: #dce4ec;
}

@media (max-width: 575px) {
  .auto-pay-pause-modal {
    padding: 24px 20px 20px;
  }

  .auto-pay-pause-title {
    font-size: 24px;
  }

  .auto-pay-pause-paragraph,
  .auto-pay-pause-checkbox {
    font-size: 16px;
  }
}
</style>
